// Shared IntersectionObserver for all DoubleRangeSlider instances
const sliderVisibilityObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const slider = entry.target as DoubleRangeSlider;
        slider.updateSlidersBoundingRect();
    }
});

const sliderResizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
        const slider = entry.target as DoubleRangeSlider;
        slider.updateSlidersBoundingRect();
    }
});

class DoubleRangeSlider extends HTMLElement {
    static formAssociated = true;
    /**
     * Minimum value of the slider.
     * @private
     */
    #min: number = 0;
    /**
     * Maximum value of the slider.
     * @private
     */
    #max: number = 100;
    /**
     * Reference to the lower (from) slider input.
     */
    fromSlider: HTMLInputElement;
    /**
     * Reference to the upper (to) slider input.
     */
    toSlider: HTMLInputElement;
    /**
     * Reference to the slider control container.
     */
    slidersControl: HTMLDivElement;
    /**
     * Reference to the draggable track element.
     */
    dragTrack: HTMLDivElement;
    /**
     * Reference to the clickable track area before the fromSlider.
     */
    clickTrackFrom: HTMLDivElement;
    /**
     * Reference to the clickable track area after the toSlider.
     */
    clickTrackTo: HTMLDivElement;
    /**
     * Cached bounding rectangle for the slider controls.
     * @private
     */
    #slidersBoundingRect?: DOMRect ;
    /**
     * Cached offset width for the slider controls.
     * @private
     */
    #slidersOffsetWidth: number = 0;
    /**
     * Listens for any changes to the fieldset element (if it exists) that contains this slider.
     * It is best for each observer to watch only one fieldset, hence this not being a global observer.
     * This is used to update the disabled state of the slider when the fieldset's disabled state changes.
     * @private
     */
    #fieldsetObserver?: MutationObserver;
    /**
     * Default value for the lower (from) slider.
     * @private
     */
    #defaultFrom?: number;
    /**
     * Default value for the upper (to) slider.
     * @private
     */
    #defaultTo?: number;
    // Add internals
    #internals: ElementInternals;


    constructor() {
        super();
        this.#internals = this.attachInternals();
        const template = document.getElementById('double-range-template') as HTMLTemplateElement;
        if (!template) throw new Error('double-range-template not found');
        this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));
        // Elements
        this.fromSlider = this.shadowRoot!.getElementById('fromSlider') as HTMLInputElement;
        this.toSlider = this.shadowRoot!.getElementById('toSlider') as HTMLInputElement;
        this.slidersControl = this.shadowRoot!.querySelector('.sliders_control') as HTMLDivElement;
        this.dragTrack = this.shadowRoot!.getElementById('dragTrack') as HTMLDivElement;
        this.clickTrackFrom = this.shadowRoot!.getElementById('clickTrackFrom') as HTMLDivElement;
        this.clickTrackTo = this.shadowRoot!.getElementById('clickTrackTo') as HTMLDivElement;
    }

    /**
     * Called when an observed attribute changes.
     * @param {string} attrName - The name of the attribute.
     * @param {string} oldValue - The old value of the attribute.
     * @param {string} newValue - The new value of the attribute.
     */
    attributeChangedCallback(attrName: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (attrName === 'min') {
                this.min = newValue;
            } else if (attrName === 'max') {
                this.max = newValue;
            } else if (attrName === 'step') {
                this.step = newValue;
            } else if (attrName === 'time-range') {
                this.timeRange = !(newValue === null);
            } else if (attrName === 'disabled') {
                this.disabled = !(newValue === null);
            } else if (attrName === 'default-from') {
                this.defaultFrom = newValue;
            } else if (attrName === 'default-to') {
                this.defaultTo = newValue;
            }
        }
    }

    /**
     * Called when the element is inserted into the DOM.
     */
    connectedCallback() {
        this.#slidersBoundingRect = this.slidersControl.getBoundingClientRect();
        this.#slidersOffsetWidth = this.slidersControl.offsetWidth;
        const self = this;

        // Functions
        function setToggleAccessible() {
            if (Number(self.toSlider.value) <= self.#min ) {
                self.toSlider.style.zIndex = "2";
            } else {
                self.toSlider.style.zIndex = "0";
            }
        }

        function updateDragTrack() {
            const from = Number(self.fromSlider.value);
            const to = Number(self.toSlider.value);
            const range = self.#max - self.#min;
            const width = self.#slidersOffsetWidth;

            let left = ((from - self.#min) / range) * width;
            let right = ((to - self.#min) / range) * width;
            if (left > right) [left, right] = [right, left];

            self.dragTrack.style.left = left + 'px';
            self.dragTrack.style.width = (right - left) + 'px';

            self.clickTrackFrom.style.width = left + 'px';
            self.clickTrackTo.style.left = right + 'px';
            self.clickTrackTo.style.width = (width - right) + 'px';
        }

        function controlSlider(controlledSlider: HTMLInputElement, otherSlider: HTMLInputElement) {
            self.fillSlider();
            if (Number(self.fromSlider.value) > Number(self.toSlider.value)) {
                controlledSlider.value = otherSlider.value;
            }
            if (controlledSlider === self.toSlider) setToggleAccessible();
            updateDragTrack();

            self.#syncFormValue();

            self.dispatchEvent(new Event('input', { bubbles: true }));
            self.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Initial setup
        setToggleAccessible();
        updateDragTrack();

        // Register this instance with the shared visibility observer
        sliderVisibilityObserver.observe(this);
        // Register this instance with the shared resize observer
        sliderResizeObserver.observe(this);
        // --- Fieldset disabled observer ---
        const fieldset = this.closest('fieldset');
        if (fieldset) {
            // Initial sync
            this.disabled = fieldset.disabled;

            // Observe changes to the fieldset's disabled attribute
            this.#fieldsetObserver = new MutationObserver(() => {
                this.disabled = fieldset.disabled;
            });
            this.#fieldsetObserver.observe(fieldset, { attributes: true, attributeFilter: ['disabled'] });
        }

        // Drag logic
        let dragStartX = 0;
        let dragStartFrom = 0;
        let dragStartTo = 0;

        this.dragTrack.addEventListener('mousedown', (e) => {
            if (this.disabled) return;
            dragStartX = e.clientX;
            dragStartFrom = parseInt(this.fromSlider.value, 10);
            dragStartTo = parseInt(this.toSlider.value, 10);
            this.dragTrack.classList.add('dragging');
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        });

        function handleMouseMove(e: MouseEvent) {
            handleDrag(e.clientX);
        }

        function handleMouseUp() {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            self.dragTrack.classList.remove('dragging');
            document.body.style.userSelect = '';
        }

        // Touch support
        this.dragTrack.addEventListener('touchstart', (e) => {
            if (this.disabled) return;
            if (e.touches.length !== 1) return;
            dragStartX = e.touches[0].clientX;
            dragStartFrom = parseInt(this.fromSlider.value, 10);
            dragStartTo = parseInt(this.toSlider.value, 10);
            this.dragTrack.classList.add('dragging');
            document.body.style.userSelect = 'none';
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);
            e.preventDefault();
        }, { passive: false });

        function handleTouchMove(e: TouchEvent) {
            if (e.touches.length !== 1) return;
            handleDrag(e.touches[0].clientX);
            e.preventDefault();
        }

        function handleTouchEnd() {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            self.dragTrack.classList.remove('dragging');
            document.body.style.userSelect = '';
        }

        // Shared drag logic
        function handleDrag(currentX: number) {
            const width = self.#slidersOffsetWidth;
            const range = self.#max - self.#min;
            const deltaPx = currentX - dragStartX;
            const deltaValue = Math.round((deltaPx / width) * range);

            let newFrom = dragStartFrom + deltaValue;
            let newTo = dragStartTo + deltaValue;

            // Clamp to min/max
            if (newFrom < self.#min) {
                newTo += (self.#min - newFrom);
                newFrom = self.#min;
            }
            if (newTo > self.#max) {
                newFrom -= (newTo - self.#max);
                newTo = self.#max;
            }

            self.fromSlider.value = newFrom.toString();
            self.toSlider.value = newTo.toString();
            self.#syncFormValue();

            self.fillSlider();
            updateDragTrack();

            self.dispatchEvent(new Event('input', { bubbles: true }));
            self.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Click tracks
        this.clickTrackFrom.addEventListener('click', (e) => {
            if (this.disabled) return;
            clickSliderTrack(e, this.fromSlider);
        });
        this.clickTrackTo.addEventListener('click', (e) => {
            if (this.disabled) return;
            clickSliderTrack(e, this.toSlider);
        });

        function clickSliderTrack(e: MouseEvent, clickedSlider: HTMLInputElement) {
            const rect = self.#slidersBoundingRect!;
            const width = self.#slidersOffsetWidth;
            const clickX = e.clientX - rect.left;
            let value = Math.round((clickX / width) * (self.#max - self.#min) + self.#min);

            // Clamp so fromSlider can't go past toSlider and vice versa
            if (clickedSlider === self.fromSlider && value > Number(self.toSlider.value)) {
                value = Number(self.toSlider.value);
            }
            if (clickedSlider === self.toSlider && value < Number(self.fromSlider.value)) {
                value = Number(self.fromSlider.value);
            }

            clickedSlider.value = value.toString();
            clickedSlider.dispatchEvent(new Event('input', { bubbles: true }));
            clickedSlider.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.fromSlider.addEventListener('input', () => controlSlider(this.fromSlider, this.toSlider));
        this.toSlider.addEventListener('input', () => controlSlider(this.toSlider, this.fromSlider));

        // Double click to reset
        this.fromSlider.addEventListener('dblclick', () => {
            if (this.disabled) return;
            this.fromSlider.value = this.#min.toString();
            controlSlider(this.fromSlider, this.toSlider);
        });
        this.toSlider.addEventListener('dblclick', () => {
            if (this.disabled) return;
            this.toSlider.value = this.#max.toString();
            controlSlider(this.toSlider, this.fromSlider);
        });
        this.dragTrack.addEventListener('dblclick', () => {
            if (this.disabled) return;
            this.fromSlider.value = this.#min.toString();
            this.toSlider.value = this.#max.toString();
            controlSlider(this.fromSlider, this.toSlider);
        });

        // Long press to reset

        // Long press helper
        function addLongPressListener(element: HTMLElement, callback: () => void, duration = 500) {
            let timer: number | null = null;
            let moved = false;

            element.addEventListener('touchstart', () => {
                if (self.disabled) return;
                moved = false; // Reset moved state
                timer = window.setTimeout(() => {
                    if (!moved) callback();
                    timer = null;
                }, duration);
            }, { passive: true });
            element.addEventListener('touchend', () => {
                if (timer) clearTimeout(timer);
                timer = null;
            });
            element.addEventListener('touchmove', () => {
                moved = true; // Set moved state to true if touch moves
                if (timer) clearTimeout(timer);
                timer = null;
            }, { passive: true });

            // Optional: support mouse long press for desktop
            element.addEventListener('mousedown', () => {
                if (self.disabled) return;
                timer = window.setTimeout(() => {
                    callback();
                    timer = null;
                }, duration);
            });
            element.addEventListener('mouseup', () => {
                if (timer) clearTimeout(timer);
                timer = null;
            });
            element.addEventListener('mouseleave', () => {
                if (timer) clearTimeout(timer);
                timer = null;
            });
            element.addEventListener('mousemove', () => {
                if (timer) clearTimeout(timer);
                timer = null;
            });
        }

        // Add long press reset for each thumb
        addLongPressListener(self.fromSlider, () => {
            self.fromSlider.value = self.#min.toString();
            controlSlider(self.fromSlider, self.toSlider);
        });
        addLongPressListener(self.toSlider, () => {
            self.toSlider.value = self.#max.toString();
            controlSlider(self.toSlider, self.fromSlider);
        });
        // Add long press reset for each thumb
        addLongPressListener(self.dragTrack, () => {
            self.fromSlider.value = self.#min.toString();
            self.toSlider.value = self.#max.toString();
            controlSlider(self.fromSlider, self.toSlider);
        });

        // Apply default values (if any) and initialize form value
        this.#applyDefaultRange();
    }

    /**
     * Called when the element is removed from the DOM.
     */
    disconnectedCallback() {
        sliderVisibilityObserver.unobserve(this);
        sliderResizeObserver.unobserve(this);
        if (this.#fieldsetObserver) {
            this.#fieldsetObserver.disconnect();
            this.#fieldsetObserver = undefined;
        }
    }

    /**
     * Updates the slider track fill based on current values.
     */
    fillSlider() {
        if (this.disabled) {
            this.toSlider.style.setProperty('--slider-track-color', 'var(--colour-form-disabled)');
            return;
        }
        const rangeDistance = Number(this.toSlider.max) - Number(this.toSlider.min);
        const fromPosition = Number(this.fromSlider.value) - Number(this.toSlider.min);
        const toPosition = Number(this.toSlider.value) - Number(this.toSlider.min);
        this.toSlider.style.setProperty('--slider-track-color', `linear-gradient(
            to right,
            var(--colour-form-disabled) 0%,
            var(--colour-form-disabled) ${(fromPosition)/(rangeDistance)*100}%,
            var(--colour-range-selected) ${((fromPosition)/(rangeDistance))*100}%,
            var(--colour-range-selected) ${(toPosition)/(rangeDistance)*100}%, 
            var(--colour-form-disabled) ${(toPosition)/(rangeDistance)*100}%, 
            var(--colour-form-disabled) 100%)`);
    }

    /**
     * Updates the cached bounding rectangle and width for the slider controls.
     */
    updateSlidersBoundingRect() {
        this.#slidersBoundingRect = this.slidersControl.getBoundingClientRect();
        this.#slidersOffsetWidth = this.slidersControl.offsetWidth;
        this.fillSlider();
    }

    static get observedAttributes() {
        return ['min', 'max', 'step', 'time-range', 'disabled', 'default-from', 'default-to'];
    }

    get min(): string {
        return this.getAttribute('min') || '';
    }

    set min(value: string) {
        if (this.getAttribute('min') != value) {
            this.setAttribute('min', value);
        }
        this.#min = Number(value);
        this.fromSlider.min = value;
        this.fromSlider.value = value;
        this.toSlider.min = value;
        this.#syncFormValue();
        this.fillSlider();
    }

    get max(): string {
        return this.getAttribute('max') || '';
    }

    set max(value: string) {
        // Only write attribute if needed (prevents loops).
        if (this.getAttribute('max') !== value) {
            this.setAttribute('max', value);
        }
        // Always sync internal state and inner inputs.
        this.#max = Number(value);
        this.fromSlider.max = value;
        this.toSlider.max = value;
        this.toSlider.value = value;
        this.#syncFormValue();
        this.fillSlider();
    }

    get value(): number[] {
        return [Number(this.fromSlider.value), Number(this.toSlider.value)];
    }

    set value(values: number[]) {
        if (values.length !== 2) {
            throw new Error('Value must be an array of two numbers.');
        }
        const [from, to] = values;
        if (from < this.#min || to > this.#max || from > to) {
            throw new Error(`Values must be within the range [${this.#min}, ${this.#max}] and from must be less than or equal to to.`);
        }
        this.fromSlider.value = from.toString();
        this.toSlider.value = to.toString();
        this.#syncFormValue();

        this.fromSlider.dispatchEvent(new Event('input', { bubbles: true }));
        this.toSlider.dispatchEvent(new Event('input', { bubbles: true }));
    }
    
    get step(): string {
        return this.getAttribute('step') || '';
    }
    
    set step(value: string) {
        if (this.getAttribute('step') != value) {
            this.setAttribute('step', value);
        }
        this.fromSlider.step = value;
        this.toSlider.step = value;
    }

    get timeRange(): boolean {
        return this.hasAttribute('time-range');
    }

    set timeRange(value: boolean) {
        if (value) {
            this.setAttribute('time-range', '');
            this.max = '1440';
            this.min = '0';
            this.step = '5'; // Default step for time range (5 minutes)
        } else {
            this.removeAttribute('time-range');
        }
    }

    get disabled(): boolean {
        return this.hasAttribute('disabled');
    }

    set disabled(value: boolean) {
        if (value) {
            this.setAttribute('disabled', '');
            this.fromSlider.disabled = true;
            this.toSlider.disabled = true;
            this.dragTrack.classList.add('disabled');
            this.clickTrackFrom.classList.add('disabled');
            this.clickTrackTo.classList.add('disabled');
        } else {
            this.removeAttribute('disabled');
            this.fromSlider.disabled = false;
            this.toSlider.disabled = false;
            this.dragTrack.classList.remove('disabled');
            this.clickTrackFrom.classList.remove('disabled');
            this.clickTrackTo.classList.remove('disabled');
        }
        this.#internals.ariaDisabled = String(value);
        this.#syncFormValue();
        this.fillSlider();
    }

    get defaultFrom(): string {
        return this.getAttribute('default-from') || '';
    }

    set defaultFrom(value: string) {
        if (this.getAttribute('default-from') !== value) {
            this.setAttribute('default-from', value);
        }
        this.#defaultFrom = Number(value);
    }

    get defaultTo(): string {
        return this.getAttribute('default-to') || '';
    }

    set defaultTo(value: string) {
        if (this.getAttribute('default-to') !== value) {
            this.setAttribute('default-to', value);
        }
        this.#defaultTo = Number(value);
    }

    /**
     * Returns the combined value of the fromSlider and toSlider as a string to make comparisons between sliders easier.
     */
    get comp(): string {
        return this.fromSlider.value + this.toSlider.value;
    }


    // Form-associated callbacks
    formDisabledCallback(disabled: boolean) {
        this.disabled = disabled;
    }

    formResetCallback() {
        this.#applyDefaultRange();
    }

    // Clamp/apply defaults
    #applyDefaultRange() {
        const from = Number.isFinite(this.#defaultFrom) ? this.#defaultFrom! : this.#min;
        const to = Number.isFinite(this.#defaultTo) ? this.#defaultTo! : this.#max;

        const clampedFrom = Math.max(this.#min, Math.min(from, this.#max));
        const clampedTo = Math.max(clampedFrom, Math.min(to, this.#max));

        this.fromSlider.value = String(clampedFrom);
        this.toSlider.value = String(clampedTo);
        this.fillSlider();
        this.#syncFormValue();
    }

    // Keep this in sync whenever slider changes
    #syncFormValue() {
        if (this.disabled) {
            this.#internals.setFormValue(null);
            return;
        }

        const [from, to] = this.value;
        const name = this.getAttribute('name') || 'range';

        // Submit two fields from one custom element
        const fd = new FormData();
        fd.append(`${name}-from`, String(from));
        fd.append(`${name}-to`, String(to));

        this.#internals.setFormValue(fd);
    }
}

customElements.define('double-range', DoubleRangeSlider);
